import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Tag } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as dashboardService from '../../../services/dashboard/dashboardService';

echarts.use([PieChart, BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
};

const PIE_COLORS = ['#1890ff', '#ff7a45', '#faad14', '#722ed1', '#13c2c2'];

const BAR_COLORS = ['#ff4d4f', '#ff7a45', '#faad14', '#1890ff', '#722ed1', '#13c2c2', '#52c41a', '#eb2f96', '#fa541c', '#2f54eb'];

/**
 * Module 4: Quality Control & Stock Analysis
 * Rate Cards + Pie (shortage/cancel reasons) + Top 10 bar
 * Supports cross-filtering: clicking shortage reason pie filters the top shortage items
 */
const QualityPanel = ({ data, channel }) => {
  const { t } = useTranslation();
  // selectedItem 保存商品编码（itemCode/sku），与 bar yAxis 一致
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemReasons, setItemReasons] = useState([]);
  const [itemReasonsLoading, setItemReasonsLoading] = useState(false);

  // 点击 Top10 商品时按需请求该 SKU 维度的缺货原因分布
  useEffect(() => {
    if (!selectedItem) {
      setItemReasons([]);
      return;
    }
    let cancelled = false;
    setItemReasonsLoading(true);
    dashboardService
      .getItemShortageReasons(selectedItem, channel)
      .then((resp) => {
        if (cancelled) return;
        if (resp?.success) {
          setItemReasons(resp.data || []);
        } else {
          setItemReasons([]);
        }
      })
      .catch(() => {
        if (!cancelled) setItemReasons([]);
      })
      .finally(() => {
        if (!cancelled) setItemReasonsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedItem, channel]);

  const handleItemClick = useCallback((params) => {
    const itemCode = params.name;
    setSelectedItem((prev) => (prev === itemCode ? null : itemCode));
  }, []);

  const onItemChartEvents = {
    click: handleItemClick,
  };

  // Helper: get color for rate value
  const getRateColor = (value, thresholdYellow = 3, thresholdRed = 5) => {
    return value >= thresholdRed ? COLORS.danger : value >= thresholdYellow ? COLORS.warning : COLORS.success;
  };

  if (!data) return null;

  // 当选中某个商品时使用接口返回的该 SKU 维度原因分布；否则显示全局分布
  const shortageReasonData = selectedItem ? itemReasons : (data.shortageReasons || []);

  // 选中商品的友好名称（优先 itemName，其次 itemCode）
  const selectedItemLabel = selectedItem
    ? (data.topShortageItems?.find((i) => i.sku === selectedItem)?.itemName || selectedItem)
    : null;

  // Shortage reason pie
  const shortageReasonOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: {
      type: 'scroll',
      orient: 'vertical',
      right: 10,
      top: 'middle',
      textStyle: { fontSize: 10 },
      itemGap: 6,
      itemWidth: 12,
      itemHeight: 12,
    },
    series: [{
      type: 'pie',
      radius: ['30%', '50%'],
      center: ['35%', '50%'],
      label: { show: false },
      labelLine: { show: false },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' },
      },
      data: shortageReasonData.map((d, i) => ({
        value: d.count,
        name: d.reason,
        itemStyle: { color: PIE_COLORS[i % PIE_COLORS.length] },
      })),
    }],
  };

  // Cancel reason pie
  const cancelReasonOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: {
      type: 'scroll',
      orient: 'vertical',
      right: 0,
      top: 'middle',
      textStyle: { fontSize: 11 },
      itemGap: 8,
    },
    series: [{
      type: 'pie',
      radius: ['30%', '55%'],
      center: ['30%', '50%'],
      label: { show: false },
      labelLine: { show: false },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' },
      },
      data: (data.cancelReasons || []).map((d, i) => ({
        value: d.count,
        name: d.reason,
        itemStyle: { color: ['#ff4d4f', '#ff7875', '#ffa39e', '#ffccc7', '#ffc4b0', '#ffd8d2'][i % 6] },
      })),
    }],
  };

  // Top shortage items bar (filtered by selected reason)
  const shortageItems = data.topShortageItems || [];
  const displayItems = shortageItems.slice(0, 10);

  const shortageBarOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 130, right: 40, top: 10, bottom: 10 },
    xAxis: { type: 'value', name: 'Count', axisLabel: { fontSize: 11 } },
    yAxis: {
      type: 'category',
      data: displayItems.map((d) => d.sku).reverse(),
      axisLabel: { fontSize: 11, width: 120, overflow: 'truncate' },
    },
    series: [{
      type: 'bar',
      data: displayItems.map((d, i) => ({
        value: d.count,
        itemStyle: {
          color: BAR_COLORS[i % BAR_COLORS.length],
          borderRadius: [0, 4, 4, 0],
        },
      })).reverse(),
      barWidth: 16,
      label: { show: true, position: 'right', fontSize: 12, fontWeight: 'bold' },
    }],
  };

  // Rate cards configuration
  const rateCards = [
    { label: t('dashboard.quality.orderOOSRate'), value: `${data.orderShortageRate}%`, color: getRateColor(data.orderShortageRate) },
    { label: t('dashboard.quality.itemOOSRate'), value: `${data.itemShortageRate}%`, color: getRateColor(data.itemShortageRate) },
    { label: t('dashboard.quality.substituteRate'), value: `${data.substitutionRate}%`, color: getRateColor(data.substitutionRate) },
    { label: t('dashboard.quality.avgShortageHandling'), value: `${data.shortageHandlingTime} ${t('dashboard.min')}`, color: COLORS.warning },
    { label: t('dashboard.quality.orderCancelRate'), value: `${data.orderCancelRate}%`, color: getRateColor(data.orderCancelRate), isAlert: true },
  ];

  return (
    <Card title={t('dashboard.quality.title')} style={{ height: '100%' }} styles={{ body: { padding: 16 } }}>
      {/* Top: 5 Rate Cards - 横向展示 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {rateCards.map((card, index) => (
          <div
            key={index}
            style={{
              flex: 1,
              background: card.isAlert ? '#fff2f0' : '#fafafa',
              border: card.isAlert ? '1px solid #ffccc7' : 'none',
              borderRadius: 8,
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              minWidth: 0,
            }}
          >
            {card.isAlert && <WarningOutlined style={{ fontSize: 14, color: COLORS.danger, marginBottom: 4 }} />}
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: card.color }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom: Charts Area */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Left: OOS Reason Breakdown Pie */}
        <div style={{ flex: '0 0 25%' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, textAlign: 'center' }}>
            {t('dashboard.quality.oosReasonBreakdown')}
            {selectedItem && (
              <Tag color="blue" closable onClose={() => setSelectedItem(null)} style={{ marginLeft: 8, marginTop: 4 }}>
                {selectedItemLabel}
              </Tag>
            )}
          </div>
          <ReactEChartsCore
            echarts={echarts}
            option={shortageReasonOption}
            style={{ height: 380 }}
            opts={{ renderer: 'canvas' }}
            showLoading={itemReasonsLoading}
            key={selectedItem || 'all'}
          />
        </div>

        {/* Center: Cancel Reason Breakdown Pie */}
        <div style={{ flex: '0 0 25%' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, textAlign: 'center' }}>{t('dashboard.quality.cancelReasonBreakdown')}</div>
          <ReactEChartsCore echarts={echarts} option={cancelReasonOption} style={{ height: 380 }} opts={{ renderer: 'canvas' }} />
        </div>

        {/* Right: Top 10 shortage items */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
            {t('dashboard.quality.topShortageItems')}
            {selectedItem && (
              <Tag color="green" closable onClose={() => setSelectedItem(null)} style={{ marginLeft: 8 }}>
                {t('dashboard.quality.selected')}: {selectedItemLabel}
              </Tag>
            )}
          </div>
          <ReactEChartsCore 
            echarts={echarts} 
            option={shortageBarOption} 
            style={{ height: 380 }} 
            opts={{ renderer: 'canvas' }} 
            onEvents={onItemChartEvents}
          />
        </div>
      </div>
    </Card>
  );
};

export default QualityPanel;
